import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ShieldCheck, Users, Crown, Zap, Euro, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const StatCard = ({ icon: Icon, label, value, sub, color = "text-primary" }) => (
  <Card className="p-5">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-wider">
      <Icon className={`h-4 w-4 ${color}`} /> {label}
    </div>
    <p className="font-heading text-3xl font-bold mt-2">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </Card>
);

const RANGES = [
  { key: "daily", label: "Diario (30 días)" },
  { key: "monthly", label: "Mensual (12 meses)" },
  { key: "yearly", label: "Anual" },
];

export default function AdminPage() {
  const qc = useQueryClient();
  const [range, setRange] = useState("daily");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const { data: stats, error } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    retry: false,
  });
  const { data: users = [] } = useQuery({
    queryKey: ["admin-users", q],
    queryFn: async () => (await api.get(`/admin/users?q=${encodeURIComponent(q)}`)).data,
    enabled: !error,
  });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="admin-forbidden">
        <ShieldCheck className="h-10 w-10 mx-auto text-muted-foreground" />
        <p className="mt-3 font-heading font-semibold">Acceso restringido a administradores</p>
      </div>
    );
  }

  const t = stats?.totals || {};
  const series = stats?.series?.[range] || [];
  const fmtPeriod = (p) =>
    range === "daily" ? p.slice(5) : range === "monthly" ? p.slice(2) : p;
  const chartData = series.map((s) => ({ ...s, label: fmtPeriod(s.period) }));

  const grant = async (email, field, value) => {
    setBusy(email + field);
    try {
      await api.post("/admin/grant", { email, field, value });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success(`${field === "comp_premium" ? "Premium" : "Admin"} ${value ? "concedido a" : "retirado de"} ${email}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-8" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-primary" /> Administración
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Estadísticas de Nuxxum y gestión de privilegios. Ingresos según transacciones registradas (IVA incluido).</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Usuarios" value={t.users ?? "—"} sub={`${t.admins || 0} administradores`} />
        <StatCard icon={Crown} label="Premium" value={t.premium ?? "—"} sub={`${t.premium_paying || 0} de pago · ${t.premium_comp || 0} concedidos`} color="text-amber-500" />
        <StatCard icon={Zap} label="Boosters vendidos" value={t.boosters_sold ?? "—"} sub={`${(t.revenue_boosters || 0).toFixed(2)} €`} color="text-indigo-500" />
        <StatCard icon={Euro} label="Ingresos totales" value={`${(t.revenue_total || 0).toFixed(2)} €`} sub={`Suscripciones: ${(t.revenue_subs || 0).toFixed(2)} €`} color="text-emerald-600" />
      </div>

      <Card className="p-5 space-y-4" data-testid="admin-charts">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading font-semibold">Evolución</h2>
          <div className="flex gap-2">
            {RANGES.map((r) => (
              <button key={r.key} data-testid={`admin-range-${r.key}`} onClick={() => setRange(r.key)}
                className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${
                  range === r.key ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">Ingresos (€)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} width={36} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} €`, "Ingresos"]} />
                <Bar dataKey="revenue" fill="#5B52EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">Registros y compras</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} width={36} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="signups" name="Registros" fill="#81C784" radius={[4, 4, 0, 0]} />
                <Bar dataKey="purchases" name="Compras" fill="#FFB74D" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4" data-testid="admin-users-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading font-semibold">Usuarios y privilegios</h2>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por email o nombre…" className="pl-9 w-64" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                <th className="py-2 pr-4">Usuario</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2 pr-4 text-center">Premium concedido</th>
                <th className="py-2 pr-4 text-center">Admin</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border/60" data-testid={`admin-user-row-${u.email}`}>
                  <td className="py-2.5 pr-4">
                    <p className="font-medium">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="py-2.5 pr-4">
                    <Badge variant={u.plan === "premium" ? "default" : "secondary"}>
                      {u.plan === "premium" ? "Premium" : "Base"}
                    </Badge>
                  </td>
                  <td className="py-2.5 pr-4 text-center">
                    <Switch data-testid={`admin-grant-premium-${u.email}`}
                      checked={!!u.comp_premium}
                      disabled={busy === u.email + "comp_premium"}
                      onCheckedChange={(v) => grant(u.email, "comp_premium", v)} />
                  </td>
                  <td className="py-2.5 pr-4 text-center">
                    <Switch data-testid={`admin-grant-admin-${u.email}`}
                      checked={!!u.is_admin}
                      disabled={busy === u.email + "is_admin"}
                      onCheckedChange={(v) => grant(u.email, "is_admin", v)} />
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-muted-foreground text-sm">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
